import apiService from "./apiService";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8080";

/**
 * List all users with their admin status
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-indexed)
 * @param {number} params.limit - Items per page
 * @param {string} params.search - Search query
 * @returns {Promise<Object>} Users list with pagination info
 */
export const listUsers = async ({ page = 1, limit = 20, search = "" } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (search) {
      params.append("search", search);
    }

    const response = await apiService.get(`/admin/users?${params.toString()}`);

    // apiService returns JSON directly (not wrapped in .data)
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to fetch users");
    }

    return response;
  } catch (error) {
    console.error("[Admin Service] Error listing users:", error);
    if (error.response) {
      throw new Error(error.response.data?.error || "Failed to fetch users");
    }
    throw error;
  }
};

/**
 * Grant admin privileges to one or multiple users
 * @param {Object} params - Grant parameters
 * @param {string|string[]} params.user_id - Single user ID (backward compatibility)
 * @param {string[]} params.user_ids - Array of user IDs (for batch operations)
 * @param {string} params.privilege - Privilege to grant ('admin' or 'global_admin')
 * @param {string} params.reason - Reason for granting privilege
 * @returns {Promise<Object>} Result message
 */
export const grantPrivilege = async ({
  user_id,
  user_ids,
  privilege,
  reason,
}) => {
  try {
    // Support both single user_id and multiple user_ids
    const targetIds = user_ids || (user_id ? [user_id] : []);

    if (targetIds.length === 0 || !privilege) {
      throw new Error("user_id(s) and privilege are required");
    }

    if (!["admin", "global_admin"].includes(privilege)) {
      throw new Error('privilege must be "admin" or "global_admin"');
    }

    const response = await apiService.post("/admin/grant", {
      user_ids: targetIds,
      privilege,
      reason: reason || "", // Send empty string if no reason provided
    });

    return response.data || response;
  } catch (error) {
    console.error("[Admin Service] Error granting privilege:", error);
    if (error.response) {
      throw new Error(
        error.response.data?.error || "Failed to grant privilege",
      );
    }
    throw error;
  }
};

/**
 * Revoke admin privileges from a user
 * @param {Object} params - Revoke parameters
 * @param {string} params.user_id - Target user ID
 * @param {string} params.privilege - Privilege to revoke ('admin' or 'global_admin')
 * @param {string} params.reason - Reason for revoking privilege
 * @returns {Promise<Object>} Result message
 */
export const revokePrivilege = async ({ user_id, privilege, reason }) => {
  try {
    if (!user_id || !privilege) {
      throw new Error("user_id and privilege are required");
    }

    if (!["admin", "global_admin"].includes(privilege)) {
      throw new Error('privilege must be "admin" or "global_admin"');
    }

    const response = await apiService.post("/admin/revoke", {
      target_user_id: user_id,
      privilege_type: privilege,
      reason: reason || "", // Send empty string if no reason provided
    });

    return response.data || response;
  } catch (error) {
    console.error("[Admin Service] Error revoking privilege:", error);
    if (error.response) {
      throw new Error(
        error.response.data?.error || "Failed to revoke privilege",
      );
    }
    throw error;
  }
};

/**
 * List all organization users from Microsoft Graph (for dropdown selection)
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search query
 * @returns {Promise<Array>} Users list
 */
export const listAllOrgUsers = async ({ search = "" } = {}) => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }

    const response = await apiService.get(
      `/admin/all-users?${params.toString()}`,
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to fetch organization users");
    }

    return response.users || [];
  } catch (error) {
    console.error("[Admin Service] Error listing all users:", error);
    throw error;
  }
};

/**
 * List all meetings across all users (Global Admin only)
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-indexed)
 * @param {number} params.limit - Items per page
 * @param {string} params.user_id - Optional filter by user ID
 * @param {string} params.search - Optional search query
 * @returns {Promise<Object>} Meetings list with pagination info
 */
export const listAllMeetings = async ({
  page = 1,
  limit = 20,
  user_id = "",
  search = "",
} = {}) => {
  try {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (user_id) {
      params.append("user_id", user_id);
    }
    if (search) {
      params.append("search", search);
    }

    const response = await apiService.get(
      `/admin/meetings?${params.toString()}`,
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to fetch meetings");
    }

    return response;
  } catch (error) {
    console.error("[Admin Service] Error listing all meetings:", error);
    if (error.response) {
      throw new Error(error.response.data?.error || "Failed to fetch meetings");
    }
    throw error;
  }
};

/**
 * Get detailed information about a specific meeting (Global Admin only)
 * @param {string} meeting_id - Meeting ID
 * @returns {Promise<Object>} Meeting details
 */
export const getMeetingDetails = async (meeting_id) => {
  try {
    if (!meeting_id) {
      throw new Error("meeting_id is required");
    }

    const response = await apiService.get(`/admin/meetings/${meeting_id}`);

    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to fetch meeting details");
    }

    return response.meeting;
  } catch (error) {
    console.error("[Admin Service] Error fetching meeting details:", error);
    if (error.response) {
      throw new Error(
        error.response.data?.error || "Failed to fetch meeting details",
      );
    }
    throw error;
  }
};

/**
 * Get salesperson performance reports across all users (Global Admin only)
 * @param {Object} params - Query parameters
 * @param {string} params.user_id - Optional filter by user ID
 * @param {number} params.days - Number of days to analyze (default: 30)
 * @returns {Promise<Object>} Salesperson reports data
 */
export const getSalespersonReports = async ({
  user_id = "",
  days = 30,
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (user_id) {
      params.append("user_id", user_id);
    }
    params.append("days", days.toString());

    const response = await apiService.get(
      `/admin/reports/salesperson?${params.toString()}`,
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to fetch salesperson reports");
    }

    return response.data;
  } catch (error) {
    console.error("[Admin Service] Error fetching salesperson reports:", error);
    if (error.response) {
      throw new Error(
        error.response.data?.error || "Failed to fetch salesperson reports",
      );
    }
    throw error;
  }
};

/**
 * Get all system prompts with metadata and custom values
 * @returns {Promise<Array>} List of prompt configs
 */
export const getSystemPrompts = async () => {
  try {
    const response = await apiService.get("/admin/prompts");
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to fetch system prompts");
    }
    return response.prompts;
  } catch (error) {
    console.error("[Admin Service] Error fetching system prompts:", error);
    throw error;
  }
};

/**
 * Update a system prompt (admin override)
 * @param {string} promptKey - The prompt key
 * @param {string} promptText - The custom prompt text
 * @returns {Promise<Object>} Result
 */
export const updateSystemPrompt = async (promptKey, promptText) => {
  try {
    const response = await apiService.put(`/admin/prompts/${promptKey}`, {
      prompt_text: promptText,
    });
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to update prompt");
    }
    return response;
  } catch (error) {
    console.error("[Admin Service] Error updating system prompt:", error);
    throw error;
  }
};

/**
 * Reset a system prompt to predefined default (remove admin override)
 * @param {string} promptKey - The prompt key
 * @returns {Promise<Object>} Result
 */
export const resetSystemPrompt = async (promptKey) => {
  try {
    const response = await apiService.delete(`/admin/prompts/${promptKey}`);
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to reset prompt");
    }
    return response;
  } catch (error) {
    console.error("[Admin Service] Error resetting system prompt:", error);
    throw error;
  }
};

/**
 * Get all admin-configurable runtime settings with metadata + current values.
 * @returns {Promise<Array>} List of setting configs
 */
export const getAdminSettings = async () => {
  try {
    const response = await apiService.get("/admin/settings");
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to fetch admin settings");
    }
    return response.settings;
  } catch (error) {
    console.error("[Admin Service] Error fetching admin settings:", error);
    throw error;
  }
};

/**
 * Update an admin setting (creates an override row).
 * @param {string} settingKey - The setting key
 * @param {*} value - The new value (will be coerced server-side)
 * @returns {Promise<Object>} Result
 */
export const updateAdminSetting = async (settingKey, value) => {
  try {
    const response = await apiService.put(`/admin/settings/${settingKey}`, {
      value,
    });
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to update setting");
    }
    return response;
  } catch (error) {
    console.error("[Admin Service] Error updating admin setting:", error);
    throw error;
  }
};

/**
 * Reset an admin setting to its registry default (removes override row).
 * @param {string} settingKey - The setting key
 * @returns {Promise<Object>} Result
 */
export const resetAdminSetting = async (settingKey) => {
  try {
    const response = await apiService.delete(`/admin/settings/${settingKey}`);
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to reset setting");
    }
    return response;
  } catch (error) {
    console.error("[Admin Service] Error resetting admin setting:", error);
    throw error;
  }
};

/**
 * Batch precompute status: HubSpot wiring, whether the sweep is enabled, and
 * the recent run history.
 */
export const getBatchPrecomputeStatus = async () => {
  try {
    const response = await apiService.get("/admin/batch-precompute/status");
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to fetch batch precompute status");
    }
    return response;
  } catch (error) {
    console.error("[Admin Service] Error fetching batch precompute status:", error);
    throw error;
  }
};

/**
 * Kick off a sweep now. Returns as soon as it is queued — the sweep itself
 * runs on the worker, so poll getBatchPrecomputeStatus for the outcome.
 */
export const runBatchPrecompute = async ({ maxCompanies, force = false } = {}) => {
  try {
    const response = await apiService.post("/admin/batch-precompute/run", {
      max_companies: maxCompanies || undefined,
      force,
    });
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to start sweep");
    }
    return response;
  } catch (error) {
    console.error("[Admin Service] Error starting batch precompute:", error);
    throw error;
  }
};

/**
 * Preview the lead pool and what the next sweep would do with it, without
 * researching anything (HubSpot reads only).
 */
export const previewBatchPrecompute = async ({ force = false } = {}) => {
  try {
    const response = await apiService.get(
      `/admin/batch-precompute/preview${force ? "?force=true" : ""}`
    );
    if (!response || response.status !== "success") {
      throw new Error(response?.error || "Failed to preview lead pool");
    }
    return response;
  } catch (error) {
    console.error("[Admin Service] Error previewing lead pool:", error);
    throw error;
  }
};

const adminService = {
  listUsers,
  listAllOrgUsers,
  grantPrivilege,
  revokePrivilege,
  listAllMeetings,
  getMeetingDetails,
  getSalespersonReports,
  getSystemPrompts,
  updateSystemPrompt,
  resetSystemPrompt,
  getAdminSettings,
  updateAdminSetting,
  resetAdminSetting,
  getBatchPrecomputeStatus,
  runBatchPrecompute,
  previewBatchPrecompute,
};

export default adminService;
